
import React, { useEffect, useState } from 'react';

// واحد بقبول التنبيه بيغطي الموقع كله: الصفحاتReact + صفحات SEO المولّدة (نفس المفتاح).
const ACK_KEY = 'tad.fraudNotice.ack.v1';
export const OPEN_FRAUD_NOTICE = 'tad:open-fraud-notice';
export const FRAUD_ACK_EVENT = 'tad:fraud-notice-ack';

export const readFraudAck = (): boolean => {
    try { return localStorage.getItem(ACK_KEY) === '1'; } catch { return false; }
};

export const writeFraudAck = () => {
    try { localStorage.setItem(ACK_KEY, '1'); } catch { /* المتصفح مانع التخزين */ }
    window.dispatchEvent(new Event(FRAUD_ACK_EVENT));
};

/** نص التنبيه نفسه — يُستخدم داخل البوب أب وفوتر الموقع حتى لا يتكرّر الكلام في مكانين. */
export const FraudNoticeBody: React.FC<{ muted?: boolean }> = ({ muted }) => (
    <div className={`text-[13.5px] sm:text-sm leading-[1.9] space-y-2.5 ${muted ? 'text-white/60' : 'text-white/75'}`}>
        <p className="font-bold text-white text-base">⚠️ تحذير هام:</p>
        <p>
            المنصة <span className="font-bold text-amber-300">لا تتواصل مع أحد</span> وليس لها مندوبون يتصلون بالعملاء.
        </p>
        <p className="font-bold text-rose-300">
            أي رسالة أو عرض يصلك باسمنا مصدره منتحل ونصاب.
        </p>
        <p>
            تواصلك معنا يتم فقط عبر طرق التواصل المنشورة على{' '}
            <a
                href="https://www.tad-ai.com"
                className="font-bold text-white underline decoration-brand/70 underline-offset-4 hover:decoration-brand"
                dir="ltr"
            >
                TAD-AI.com
            </a>
            .
        </p>
        <p>
            تخصصنا فقط هو <span className="font-bold text-white">التحليلات بالذكاء الاصطناعي</span>.
        </p>
        <p>
            <span className="font-bold text-amber-300">لا ندير محافظ</span> ولا نطلب أموالًا ولا نقبل إيداعًا أو سحبًا.
        </p>
        <p className="font-semibold text-white/85 pt-1">وجب التنويه.</p>
    </div>
);

const FraudWarningModal: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);

    useEffect(() => {
        if (!readFraudAck()) setOpen(true);
        const reopen = () => { setAcknowledged(false); setOpen(true); };
        window.addEventListener(OPEN_FRAUD_NOTICE, reopen);
        return () => window.removeEventListener(OPEN_FRAUD_NOTICE, reopen);
    }, []);

    useEffect(() => {
        if (!open) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [open]);

    if (!open) return null;

    const accept = () => {
        if (!acknowledged) return;
        writeFraudAck();
        setOpen(false);
    };

    return (
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center px-4 py-8 bg-black/85 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fraud-notice-title"
        >
            <div className="w-full max-w-xl max-h-[88vh] overflow-y-auto rounded-3xl border border-rose-500/35 bg-[#0B1120] p-6 sm:p-8 shadow-[0_0_70px_rgba(240,178,51,0.18)]">
                <div className="flex items-center gap-3 mb-1">
                    <span className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400/25 to-rose-500/25 border border-amber-400/40 text-xl" aria-hidden="true">🛡️</span>
                    <div>
                        <h2 id="fraud-notice-title" className="text-white font-bold text-lg leading-tight">تنبيه رسمي من TAD-AI</h2>
                        <p className="text-rose-300/90 text-xs font-semibold mt-1">قراءة هذا التنبيه إلزامية قبل تصفّح الموقع</p>
                    </div>
                </div>

                <div className="mt-5 mb-6 p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-white/[0.09]">
                    <FraudNoticeBody />
                </div>

                <label className="flex items-start gap-3 cursor-pointer select-none p-4 rounded-2xl bg-white/[0.02] border border-white/[0.08] hover:border-amber-400/40 transition-colors">
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                        className="mt-0.5 h-5 w-5 flex-shrink-0 accent-rose-500 cursor-pointer"
                    />
                    <span className="text-sm text-white/85 leading-relaxed">
                        أقرّ أنني <span className="font-bold text-white">قرأت هذا التنبيه وفهمت تماماً</span> ما تحتويه الرسالة من تحذيرات.
                    </span>
                </label>

                <button
                    type="button"
                    onClick={accept}
                    disabled={!acknowledged}
                    aria-disabled={!acknowledged}
                    className={`mt-5 w-full rounded-xl py-3.5 px-6 font-bold text-[15px] transition-all ${
                        acknowledged
                            ? 'bg-gradient-to-l from-amber-500 to-rose-600 text-white shadow-lg hover:opacity-90'
                            : 'bg-white/[0.04] text-white/30 border border-white/10 cursor-not-allowed'
                    }`}
                >
                    لقد فهمت هذا
                </button>
            </div>
        </div>
    );
};

export default FraudWarningModal;

/**
 * بعد ما الزائر يوافق: شريط رفيع ثابت تحت الهيدر يعرض الملخص طول تصفحه للمنصة،
 * والضغط عليه يفتح التنبيه كامل من جديد.
 */
export const FraudNoticeBar: React.FC = () => {
    const [visible, setVisible] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const sync = () => setVisible(readFraudAck());
        sync();
        window.addEventListener(FRAUD_ACK_EVENT, sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener(FRAUD_ACK_EVENT, sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    useEffect(() => {
        const head = document.querySelector('header');
        if (!head || typeof ResizeObserver === 'undefined') return;
        const measure = () => setHeaderHeight(Math.round(head.getBoundingClientRect().height));
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(head);
        return () => observer.disconnect();
    }, [visible]);

    if (!visible) return null;

    return (
        <div className="fixed left-0 right-0 z-[95]" style={{ top: headerHeight }}>
            <div className="bg-[#12082A]/95 backdrop-blur-md border-y border-rose-500/25">
                <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-6 xl:px-8 py-[5px] flex items-center gap-2 sm:gap-3">
                    <span aria-hidden="true" className="flex-shrink-0 text-[13px]">🛡️</span>
                    <p className="min-w-0 flex-1 truncate text-[11.5px] sm:text-xs leading-tight text-white/70">
                        المنصة <span className="font-bold text-amber-300">لا تتواصل مع أحد</span> — أي رسالة أو عرض يصلك باسمنا{' '}
                        <span className="font-bold text-rose-300">منتحل ونصاب</span>. التواصل فقط عبر{' '}
                        <span className="font-bold text-white" dir="ltr">TAD-AI.com</span>
                        <span className="hidden lg:inline"> • لا ندير محافظ ولا نقبل إيداعًا أو سحبًا</span>
                    </p>
                    <button
                        type="button"
                        onClick={() => window.dispatchEvent(new Event(OPEN_FRAUD_NOTICE))}
                        className="flex-shrink-0 rounded-md border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 text-amber-200 text-[10.5px] sm:text-[11px] font-bold px-2.5 py-1 transition-colors"
                    >
                        التفاصيل كاملة
                    </button>
                </div>
            </div>
        </div>
    );
};
